/**
 * Navigation Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Product template.
 *
 * @namespace navigation
 */

import { register } from '@shopify/theme-sections';
import stickybits from 'stickybits';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import '../components/search';

export default register('header', {
  onLoad() {
    this.isFixed = this.container.dataset.fixed || false;
    const sectionHeaderEl = document.querySelector('#shopify-section-header');
    this.header = this.container.querySelector('.header');
    const headerEl = this.container.querySelector('.header');

    const self = this;

    this.calculateNavDropdownMaxSize();

    const btnMenuOpen = this.container.querySelector('.nav-toggle');
    btnMenuOpen.addEventListener(
      'click',
      this.btnMenuClickOpenHandler.bind(this)
    );

    const btnMenuClose = this.container.querySelector('.nav-close');
    btnMenuClose.addEventListener(
      'click',
      this.btnMenuClickCloseHandler.bind(this)
    );

    const navOverlay = this.container.querySelector('.fader--nav');
    navOverlay.addEventListener(
      'click',
      this.btnMenuClickCloseHandler.bind(this)
    );

    // Search toggle
    window.searchIsVisible = false;
    const btnSearch = this.container.querySelector('.header__btn--search');
    btnSearch.addEventListener('click', this.toggleModal.bind(this));

    const navLinks = this.container.querySelectorAll('.nav__item--toggle');
    navLinks.forEach(function(el) {
      el.addEventListener('click', self.navLinkClickHandler.bind(self));
    });

    let lastKnownScrollPosition = window.pageYOffset;
    let ticking = false;

    if (lastKnownScrollPosition <= 10) {
      headerEl.classList.add('is-top');
    } else {
      headerEl.classList.remove('is-top');
    }

    if (this.isFixed === 'true') {
      stickybits(sectionHeaderEl, {
        useStickyClasses: true,
      });
    }

    window.addEventListener('scroll', function(event) {
      lastKnownScrollPosition = window.window.pageYOffset;
      ticking = false;

      window.requestAnimationFrame(function() {
        if (!ticking) {
          if (lastKnownScrollPosition <= 10) {
            headerEl.classList.add('is-top');
          } else {
            headerEl.classList.remove('is-top');
          }
          ticking = true;
        }
      });
    });
  },

  calculateNavDropdownMaxSize() {
    let headerSize;
    let navDropdownMaxHeight;
    const header = this.container;
    const navDropdowns = this.container.querySelectorAll('.nav__dropdown');

    window.addEventListener('load', function() {
      headerSize = header.getBoundingClientRect();
      navDropdownMaxHeight = Math.floor(window.innerHeight - headerSize.height);

      navDropdowns.forEach(function(el) {
        if (el && window.innerWidth > window.theme.breakpoints.large) {
          el.style.cssText = 'max-height: ' + navDropdownMaxHeight + 'px';
        } else {
          el.removeAttribute('style');
        }
      });
    });

    window.addEventListener('resize', function() {
      headerSize = header.getBoundingClientRect();
      navDropdownMaxHeight = Math.floor(window.innerHeight - headerSize.height);

      navDropdowns.forEach(function(el) {
        if (el && window.innerWidth > window.theme.breakpoints.large) {
          el.style.cssText = 'max-height: ' + navDropdownMaxHeight + 'px';
        } else {
          el.removeAttribute('style');
        }
      });
    });
  },

  /**
   * Click handler for search button
   */
  collapseDropdown() {
    let activeDropdowns = this.header.querySelectorAll(
      '.nav__item.nav__item--has-child.is-expanded'
    );
    let navToggles = this.container.querySelectorAll(
      '.nav__item.nav__item--has-child.is-expanded .nav__item--toggle'
    );

    document.body.classList.remove('dropdown-is-active');

    activeDropdowns.forEach(function(el) {
      if (el) {
        el.classList.remove('is-expanded');
        el.classList.add('is-collapsed');
      }
    });

    navToggles.forEach(function(el) {
      if (el) {
        el.setAttribute('aria-expanded', false);
      }
    });
  },

  /**
   * .header__btn--menu click handler
   */
  btnMenuClickOpenHandler() {
    document.body.classList.toggle('menu-is-expanded');
    disableBodyScroll(this);

    const subMenuParents = document.querySelectorAll('.nav__item--has-child');

    subMenuParents.forEach(function(el) {
      if (el.classList.contains('is-expanded')) {
        el.classList.remove('is-expanded');
        setTimeout(function() {
          el.classList.add('is-collapsed');
        }, 200);
      }
    });
  },

  btnMenuClickCloseHandler() {
    document.body.classList.remove('menu-is-expanded');
    enableBodyScroll(this);

    const subMenuParents = document.querySelectorAll('.nav__item--has-child');

    subMenuParents.forEach(function(el) {
      if (el.classList.contains('is-expanded')) {
        el.classList.remove('is-expanded');
        el.classList.add('is-collapsed');
      }
    });
  },

  /**
   * .nav__item.has-child > a click handler
   */
  navLinkClickHandler(event) {
    event.preventDefault();

    // Menu item toggle
    const navLinks = document.querySelectorAll('.nav__item--toggle');
    const navItem = event.currentTarget.parentElement;
    const thisNavItem = event.currentTarget;

    // Hide the search modal if it's showing
    if (window.searchIsVisible) {
      this.hideModal();
    }

    if (navItem.classList.contains('is-expanded')) {
      thisNavItem.setAttribute('aria-expanded', false);
      navItem.classList.remove('is-expanded');
      setTimeout(function() {
        navItem.classList.add('is-collapsed');
      }, 200);
      document.body.classList.remove('dropdown-is-active');
    } else {
      navLinks.forEach((el) =>
        el.parentElement.classList.remove('is-expanded')
      );
      navLinks.forEach((el) => el.parentElement.classList.add('is-collapsed'));
      navLinks.forEach((el) => el.setAttribute('aria-expanded', false));
      navItem.classList.remove('is-collapsed');
      navItem.classList.add('is-expanded');
      document.body.classList.add('dropdown-is-active');
      thisNavItem.setAttribute('aria-expanded', true);
    }

    return true;
  },

  /**
   * Toggles the search modal
   */
  toggleModal() {
    if (window.searchIsVisible) {
      this.hideModal();
    } else {
      this.showModal();
    }
  },

  /**
   * Expands the search modal
   */
  showModal() {
    const searchToggleButton = document.querySelector('.header__btn--search');
    const searchCloseButton = document.querySelector('.btn--search-close');
    const searchModal = document.querySelector('.search-modal');
    const searchInput = document.querySelector('.search-input');

    disableBodyScroll(this);
    document.body.classList.add('search-is-visible');
    searchToggleButton.setAttribute('aria-expanded', true);
    searchCloseButton.setAttribute('aria-expanded', true);
    searchModal.removeAttribute('hidden');

    if (searchInput) {
      searchInput.focus();
    }
    window.searchIsVisible = true;
    this.collapseDropdown();
  },

  /**
   * Collapses the search modal
   */
  hideModal() {
    const searchToggleButton = document.querySelector('.header__btn--search');
    const searchCloseButton = document.querySelector('.btn--search-close');
    const searchModal = document.querySelector('.search-modal');
    const searchInput = document.querySelector('.search-input');

    enableBodyScroll(this);
    document.body.classList.remove('search-is-visible');
    searchToggleButton.setAttribute('aria-expanded', false);
    searchCloseButton.setAttribute('aria-expanded', false);
    searchModal.setAttribute('hidden', 'hidden');

    if (searchInput) {
      searchInput.blur();
    }
    window.searchIsVisible = false;
  },
});
